"use client";

import type { ReactNode } from "react";
import { Label } from "@qrody/ui";
import type { AnonymousFormat } from "@qrody/api-client/render";

/** Generic form-field wrapper — label + child input. */
export function Field({
  id,
  label,
  children,
}: {
  id: string;
  label: string;
  children: ReactNode;
}) {
  return (
    <div className="space-y-2">
      <Label htmlFor={id}>{label}</Label>
      {children}
    </div>
  );
}

/** Boxed pane holding the QR preview image. The box has no min-height —
 *  it grows / shrinks with its content, so the display-size slider scales
 *  the visible card as well as the QR itself. `fill` keeps a same-height
 *  flexbox shape for compare-mode where two panes sit side-by-side. */
export function PreviewPane({
  label,
  children,
  fill = false,
}: {
  label: string;
  children: ReactNode;
  fill?: boolean;
}) {
  return (
    // `min-w-[min(16rem,100%)]` clamps the floor at 256 px **or** the
    // parent width if narrower — so the floor never exceeds the cell
    // (and therefore never the viewport). `w-fit max-w-full` lets the
    // box hug the QR above that floor. `overflow-hidden` is the safety
    // net: even if a child wanted to spill out (rounding on `mm`-to-px,
    // pixelated upscaling artefacts, …), the box clips it instead of
    // letting the QR escape past its border.
    <div
      className={`mx-auto flex w-fit min-w-[min(16rem,100%)] max-w-full flex-col items-center justify-center overflow-hidden rounded-lg bg-muted p-3 ${
        fill ? "h-full" : ""
      }`}
    >
      <span className="mb-2 text-[11px] uppercase tracking-wider text-muted-foreground">
        {label}
      </span>
      {children}
    </div>
  );
}

/**
 * Render the client-side preview blob.
 *
 * - `compact` caps the rendered QR at 256 px so the dual compare-view stays
 *   balanced; `compact = false` lets the image fill the parent pane (used
 *   by the single-pane default so the QR uses the full column height).
 * - `displayMm` (single-pane only) overrides the natural sizing with an
 *   explicit physical width — lets the user hold a phone to the screen to
 *   verify the code scans at its real-world print size before saving.
 */
export function renderClientPane(
  blob: string | null,
  format: AnonymousFormat,
  compact: boolean,
  displayMm?: number,
): ReactNode {
  if (format === "pdf") {
    return <p className="text-sm text-muted-foreground">PDF — nur Server</p>;
  }
  if (!blob) {
    // While the first render is in flight, hold the same physical size as
    // the QR will occupy — otherwise the pane collapses to label-height
    // and the layout jumps once the blob arrives.
    if (displayMm && !compact) {
      return (
        <div
          className="aspect-square max-w-full"
          style={{ width: `${displayMm}mm` }}
          aria-hidden
        />
      );
    }
    return <p className="text-sm text-muted-foreground">…</p>;
  }
  if (displayMm && !compact) {
    // Wrapper sizes itself to the user-requested physical width and stays
    // square via aspect-square. `max-w-full` clamps to the parent column
    // on narrow viewports; height follows from the aspect ratio so the
    // QR never gets distorted. No `max-h-full` here — the pane itself
    // has no min-height anymore, so the box grows with the wrapper
    // instead of clipping it.
    return (
      <div
        className="aspect-square max-w-full"
        style={{ width: `${displayMm}mm` }}
      >
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src={blob}
          alt="QR-Vorschau"
          className="h-full w-full object-contain"
          style={{ imageRendering: "pixelated" }}
        />
      </div>
    );
  }
  const sizing = compact ? "max-h-64 max-w-full" : "max-h-full max-w-full flex-1 object-contain";
  // eslint-disable-next-line @next/next/no-img-element
  return <img src={blob} alt="QR-Vorschau" className={sizing} />;
}

export function renderServerPane(
  blob: string | null,
  format: AnonymousFormat,
  pending: boolean,
  err: string | null,
  compact: boolean,
): ReactNode {
  if (!blob) {
    return <p className="text-sm text-muted-foreground">{pending ? "…" : err ?? ""}</p>;
  }
  if (format === "pdf") {
    return (
      <object
        data={blob}
        type="application/pdf"
        className={compact ? "h-64 w-full" : "h-full min-h-64 w-full flex-1"}
      >
        <p className="text-sm text-muted-foreground">PDF</p>
      </object>
    );
  }
  const sizing = compact ? "max-h-64 max-w-full" : "max-h-full max-w-full flex-1 object-contain";
  // eslint-disable-next-line @next/next/no-img-element
  return <img src={blob} alt="QR-Vorschau (Server)" className={sizing} />;
}
